import React from "react";
import { useReveal } from "@/lib/useReveal";
import { Search, HeartPulse, LineChart, Handshake, ArrowUpRight } from "lucide-react";

const LENSES = [
  {
    icon: Search,
    title: "Find Customers",
    body: "Ranked leads with buying intent from Reddit, forums, LinkedIn and X — pick any platform per scan.",
    status: "live",
    accent: "bg-lm-primary text-white",
  },
  {
    icon: HeartPulse,
    title: "Pain Points",
    body: "Cluster complaints and frustrations across the market - see what to fix or build next.",
    status: "live",
    accent: "bg-lm-tint2 text-lm-primary",
  },
  {
    icon: LineChart,
    title: "Market Analysis",
    body: "Share of voice, sentiment shifts, and competitor weaknesses in one live view.",
    status: "live",
    accent: "bg-lm-tint text-lm-text",
  },
  {
    icon: Handshake,
    title: "Partners & Influencers",
    body: "Surface collaborators, affiliates and creators (incl. TikTok) who already move your niche.",
    status: "live",
    accent: "bg-lm-tint3 text-lm-secondary",
  },
];

const statusStyle = {
  live: "bg-lm-tint2 text-lm-primary",
  shipping: "bg-lm-tint3 text-lm-secondary",
};

export default function Lenses() {
  const [headRef, headVisible] = useReveal();
  const [gridRef, gridVisible] = useReveal(0.1);

  return (
    <section id="lenses" className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-24 lm-cv">
      <div
        ref={headRef}
        className={`max-w-3xl mb-12 lm-reveal ${headVisible ? "is-visible" : ""}`}
      >
        <div className="text-xs font-mono uppercase tracking-widest text-lm-secondary mb-3">
          Four lenses. One scan.
        </div>
        <h2 className="text-3xl md:text-5xl font-display font-semibold leading-tight tracking-tight">
          Every scan feeds every view.
        </h2>
        <p className="mt-5 text-lg text-lm-text2 leading-relaxed">
          You never re-run the same work twice. A single scan populates all four discovery lenses - find, feel, analyze, partner.
        </p>
      </div>

      <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lm-stagger">
        {LENSES.map((l, i) => (
          <div
            key={l.title}
            style={{ "--i": i }}
            className={`group relative bg-lm-surface border border-lm-border rounded-lg p-6 hover:border-lm-primary/40 hover:shadow-[0_10px_28px_-12px_rgba(31,74,56,0.25)] hover:-translate-y-1 transition-all duration-300 lm-reveal ${gridVisible ? "is-visible" : ""}`}
          >
            <div className={`w-11 h-11 rounded-lg ${l.accent} flex items-center justify-center mb-4`}>
              <l.icon className="w-5 h-5" strokeWidth={1.8} />
            </div>
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-lg font-display font-semibold">{l.title}</h3>
              <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${statusStyle[l.status]}`}>
                {l.status === "live" ? "Live" : "Ships next"}
              </span>
            </div>
            <p className="text-sm text-lm-text2 leading-relaxed">{l.body}</p>
            <ArrowUpRight className="absolute top-5 right-5 w-4 h-4 text-lm-text3 opacity-0 group-hover:opacity-100 group-hover:text-lm-primary transition-all" />
          </div>
        ))}
      </div>
    </section>
  );
}
